import type { PickemQuestion } from '@low6-pickem/api-types';
import type { TeamPair } from '@/types/domain';
import { getOptionId } from './id-helpers';
import { findOptionByTitle } from './id-helpers';
import { getLocalizedField } from './localized-field';
import {
  getSportLogoUrl,
  getFileExtForSport,
  mainMarketMeasureFor,
  usesCountryFlags,
} from '@low6-pickem/sports-config';
import { normalizeTeamLogoUrl } from './normalizeTeamLogoUrl';
import { isLowResImages, toLowResImageUrl } from './lowResImages';
import { isLeagueIp, nonIpTeamFolder, getTeamAssetFolder } from './leagueIp';
import { getStatTypeDisplayName } from '@low6-pickem/ui/utils/stat-type-display';
import { formatPlayerNameAsInitialSurname } from '@low6-pickem/ui/utils/name-format';
import i18n from '@/i18n';
import { findOverUnderOption } from './over-under-option';

const COLORS = [
  { color: '#c8102e', accent: '#000000' },
  { color: '#003f7f', accent: '#ffffff' },
  { color: '#006847', accent: '#ffffff' },
  { color: '#fcb514', accent: '#000000' },
];

// Stat-code → display-name mapping now lives in the shared UI package
// (@low6-pickem/ui/utils/stat-type-display) so the admin preview over/under
// adapter and this file stay in lockstep. `getStatTypeDisplayName(code)` is a
// drop-in for the old `statTypeDisplayMap[code] || code`.

/**
 * Whether a fixture has two different sides to print as "Away @ Home".
 *
 * A PGA round is stored as a fixture whose home and away are BOTH the tournament — there is no
 * opponent — so without this every golfer card read "BAN @ BAN". Missing ids count as a real
 * matchup, which keeps every team sport exactly as it was.
 */
export function fixtureHasOpponent(
  data: { homeTeamId?: unknown; awayTeamId?: unknown } | null | undefined
): boolean {
  if (!data?.homeTeamId || !data?.awayTeamId) return true;
  return String(data.homeTeamId) !== String(data.awayTeamId);
}

type OptionToTeamPairConfig = {
  idPrefix: string;
  resolveName: (
    option: PickemQuestion['options'][0] | undefined,
    index: number,
    locale: string
  ) => string;
};

function buildOptionAsTeamPair(
  option: PickemQuestion['options'][0] | undefined,
  index: number,
  question: PickemQuestion | undefined,
  config: OptionToTeamPairConfig
): TeamPair {
  const colorPair = COLORS[index % COLORS.length];
  const locale = i18n.language;

  // Extract player position - prefer option.grading_data, fallback to question metadata
  const position =
    option?.grading_data?.playerPosition ||
    question?.question_metadata?.grading?.config?.playerPosition;

  // Extract fixture data - prefer option.grading_data.fixtureData, fallback to question.sport_match_data
  const fixtureData = option?.grading_data?.fixtureData;
  const matchData = question?.sport_match_data;
  let fixture:
    | {
        homeTeam: string;
        awayTeam: string;
        homeTeamAbbreviation: string;
        awayTeamAbbreviation: string;
        matchTime: string;
      }
    | undefined = undefined;
  let isPlayerTeam: 'home' | 'away' | undefined = undefined;

  if (fixtureData) {
    const hasOpponent = fixtureHasOpponent(fixtureData);
    fixture = {
      homeTeam: fixtureData.homeTeamName,
      awayTeam: fixtureData.awayTeamName,
      homeTeamAbbreviation: hasOpponent ? fixtureData.homeTeamAbbreviation : '',
      awayTeamAbbreviation: hasOpponent ? fixtureData.awayTeamAbbreviation : '',
      matchTime: formatMatchTime(fixtureData.scheduledDate),
    };

    const teamId = option?.grading_data?.teamId;
    if (teamId !== undefined && teamId !== null) {
      if (fixtureData.homeTeamId === String(teamId)) {
        isPlayerTeam = 'home';
      } else if (fixtureData.awayTeamId === String(teamId)) {
        isPlayerTeam = 'away';
      }
    }
  } else if (matchData) {
    const hasOpponent = fixtureHasOpponent(matchData);
    fixture = {
      homeTeam: matchData.homeTeamName,
      awayTeam: matchData.awayTeamName,
      homeTeamAbbreviation: hasOpponent ? matchData.homeTeamAbbreviation : '',
      awayTeamAbbreviation: hasOpponent ? matchData.awayTeamAbbreviation : '',
      matchTime: formatMatchTime(matchData.scheduledDate),
    };

    const teamId = option?.grading_data?.teamId;
    if (teamId !== undefined && teamId !== null) {
      if (matchData.homeTeamId === String(teamId)) {
        isPlayerTeam = 'home';
      } else if (matchData.awayTeamId === String(teamId)) {
        isPlayerTeam = 'away';
      }
    }
  }

  const statTypeCode = question?.stat_type_code;
  const liveStatType = statTypeCode ? getStatTypeDisplayName(statTypeCode) : undefined;

  const normalizeAssetUrl = (url?: string | null) => (url ? normalizeTeamLogoUrl(url) : url);

  // Team tri-code the player plays for. Populated by the question factories
  // when a question is feed-based; falls back to the question-level metadata
  // shortcode if option-level isn't set.
  const teamShortcode =
    option?.grading_data?.teamShortcode ||
    question?.question_metadata?.grading?.config?.teamShortcode;

  return {
    id: option ? getOptionId(option) : `${config.idPrefix}${index + 1}`,
    name: config.resolveName(option, index, locale),
    color: colorPair.color,
    accent: colorPair.accent,
    jerseyUrl: normalizeAssetUrl(option?.option_image),
    thirdImageUrl: normalizeAssetUrl(option?.option_third_image),
    popularityPct: option?.popularity_pct,
    position,
    teamShortcode,
    fixture,
    isPlayerTeam,
    liveValue: option?.grading_data?.liveValue,
    liveStatType,
  };
}

function createTeamOption(
  option: PickemQuestion['options'][0] | undefined,
  index: number,
  question?: PickemQuestion
): TeamPair {
  return buildOptionAsTeamPair(option, index, question, {
    idPrefix: 'team',
    resolveName: (opt, i, locale) =>
      opt ? (getLocalizedField(opt, 'title', locale) ?? `Team ${i + 1}`) : `Team ${i + 1}`,
  });
}

function createPlayerOption(
  option: PickemQuestion['options'][0] | undefined,
  index: number,
  question?: PickemQuestion
): TeamPair {
  return buildOptionAsTeamPair(option, index, question, {
    idPrefix: 'player',
    resolveName: (opt, i, locale) => {
      const title = opt ? (getLocalizedField(opt, 'title', locale) ?? '') : '';
      return opt ? formatPlayerNameAsInitialSurname(title) || `Player ${i + 1}` : `Player ${i + 1}`;
    },
  });
}

export function getTeamsForQuestion(question: PickemQuestion): TeamPair[] {
  // Return ALL options, not just the first 2
  return question.options.map((option, index) => createTeamOption(option, index, question));
}

export function getPlayersForQuestion(question: PickemQuestion): TeamPair[] {
  return question.options.map((option, index) => createPlayerOption(option, index, question));
}

/**
 * Custom option format for CustomPick component
 */
export interface CustomOptionData {
  id: string;
  label: string;
  imageUrl?: string | null;
  popularityPct?: number;
  thirdImageUrl?: string | null;
}

/**
 * Get options for custom question type in CustomPick format
 */
export function getCustomOptionsForQuestion(question: PickemQuestion): CustomOptionData[] {
  const locale = i18n.language;
  return question.options.map((option, index) => ({
    id: getOptionId(option),
    label:
      getLocalizedField(option, 'title', locale) ||
      getLocalizedField(option, 'option_text', locale) ||
      `Option ${index + 1}`,
    imageUrl: option.option_image ? normalizeTeamLogoUrl(option.option_image) : null,
    popularityPct: option.popularity_pct,
    thirdImageUrl: option.option_third_image
      ? normalizeTeamLogoUrl(option.option_third_image)
      : null,
  }));
}

export function getPercentagesForPropQuestion(question: PickemQuestion) {
  const yesOption = question.options.find(opt => opt.title_en?.toLowerCase().includes('yes'));
  const noOption = question.options.find(opt => opt.title_en?.toLowerCase().includes('no'));

  return {
    yes: yesOption?.popularity_pct == undefined ? 50 : yesOption?.popularity_pct,
    no: noOption?.popularity_pct == undefined ? 50 : noOption?.popularity_pct,
  };
}

export function getPercentagesForOverUnderQuestion(question: PickemQuestion) {
  const overOption = findOverUnderOption(question, 'over');
  const underOption = findOverUnderOption(question, 'under');

  return {
    over: overOption?.popularity_pct == undefined ? 50 : overOption?.popularity_pct,
    under: underOption?.popularity_pct == undefined ? 50 : underOption?.popularity_pct,
  };
}

export function getThirdImagesForPropQuestion(question: PickemQuestion) {
  const yesOption = question.options.find(opt => opt.title_en?.toLowerCase().includes('yes'));
  const noOption = question.options.find(opt => opt.title_en?.toLowerCase().includes('no'));

  return {
    yes: yesOption?.option_third_image
      ? normalizeTeamLogoUrl(yesOption.option_third_image)
      : undefined,
    no: noOption?.option_third_image
      ? normalizeTeamLogoUrl(noOption.option_third_image)
      : undefined,
  };
}

export function findOptionByAnswer(
  question: PickemQuestion,
  answer: 'yes' | 'no'
): string | undefined {
  // First try to find by title containing yes/no
  let option = findOptionByTitle(question, answer);

  // If not found, map by position: first option = yes, second = no
  if (!option && question.options.length >= 2) {
    option = answer === 'yes' ? question.options[0] : question.options[1];
  }

  return option ? getOptionId(option) : undefined;
}

// Name abbreviation now lives in the shared UI package
// (@low6-pickem/ui/utils/name-format) so the admin question previews format
// names identically. Re-exported here for existing imports.
export { formatPlayerNameAsInitialSurname } from '@low6-pickem/ui/utils/name-format';

/**
 * Extracts entity data (player or team) from OVER_UNDER question metadata
 * Handles both player over/under and team over/under questions
 * Falls back to option-level grading_data for backwards compatibility
 */
export function extractPlayerData(question: PickemQuestion): {
  name: string;
  position: string;
} {
  // First try question-level metadata (new format)
  const config = question.question_metadata?.grading?.config;

  // Check for player data first
  if (config?.playerName) {
    return {
      name: config.playerName,
      position: config.playerPosition || '',
    };
  }

  // Check for team data in config (team over/under questions)
  if (config?.teamName) {
    return {
      name: config.teamName,
      position: '', // Teams don't have positions
    };
  }

  // Fallback to option-level grading_data (always populated by factory)
  const firstOption = question.options?.[0];
  const gradingData = firstOption?.grading_data;

  // Check for player data in option
  if (gradingData?.playerName) {
    return {
      name: gradingData.playerName,
      position: gradingData.playerPosition || '',
    };
  }

  // Check for team data in option (team over/under questions)
  if (gradingData?.teamName) {
    return {
      name: gradingData.teamName,
      position: '', // Teams don't have positions
    };
  }

  return {
    name: 'Unknown Player',
    position: '',
  };
}

/**
 * The label beside an over/under line. A tennis or MMA Total counts games, sets or rounds rather
 * than a player stat, so it reads "Over 22.5 Games", not "Games Won" or the score's "Points".
 */
function statLabel(question: PickemQuestion, statType: string): string {
  const measure =
    String(question.question_type).toLowerCase() === 'total'
      ? mainMarketMeasureFor(
          (question as { sport_external_id?: string | null }).sport_external_id ?? undefined,
          'total',
          statType
        )
      : undefined;
  return measure?.label ?? getStatTypeDisplayName(statType);
}

/**
 * Extracts stat data from OVER_UNDER question metadata and subtext
 * Falls back to option-level grading_data and question.margin for backwards compatibility
 */
export function extractStatData(question: PickemQuestion): {
  threshold: number;
  displayName: string;
  type: string;
} {
  // First try question-level metadata (new format)
  const config = question.question_metadata?.grading?.config;

  if (config?.threshold !== undefined) {
    const statType = config.statType || '';
    return {
      threshold: config.threshold,
      displayName: statLabel(question, statType),
      type: statType,
    };
  }

  // Fallback to option-level grading_data (always populated by factory)
  const firstOption = question.options?.[0];
  const gradingData = firstOption?.grading_data;
  if (gradingData?.threshold !== undefined) {
    // Get stat type from question.stat_type_code field
    const statType = question.stat_type_code || '';
    return {
      threshold: gradingData.threshold,
      displayName: statLabel(question, statType),
      type: statType,
    };
  }

  // Final fallback to question.margin field
  if (question.margin !== undefined && question.margin !== null) {
    const statType = question.stat_type_code || '';
    return {
      threshold: question.margin,
      displayName: statLabel(question, statType),
      type: statType,
    };
  }

  return {
    threshold: 0,
    displayName: '',
    type: '',
  };
}

/**
 * Formats match scheduled date to display format (e.g., "Sun, 1:00pm")
 */
export function formatMatchTime(scheduledDate?: string): string {
  if (!scheduledDate) return '';

  const date = new Date(scheduledDate);
  const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${dayOfWeek}, ${time}`;
}

/**
 * Gets the appropriate league logo URL based on the sport
 * @param sport - The sport identifier ('NFL', 'NBA', 'NHL')
 * @returns The league logo URL
 */
export function getLeagueLogoUrl(sport?: string): string {
  return getSportLogoUrl(sport);
}

/**
 * Extracts the player's team logo URL from OVER_UNDER question metadata
 * Used to display team badge in player over/under questions
 * Falls back to generating URL from teamShortcode if teamLogoUrl isn't present
 */
export function extractPlayerTeamLogo(question: PickemQuestion): string {
  // First try question-level metadata (new format with teamLogoUrl)
  const config = question.question_metadata?.grading?.config;

  // Only show team badge for Player Over/Under (when there's a playerName)
  // For Team Over/Under, the main image IS the team logo, so don't show badge
  if (!config?.playerName && config?.teamName) {
    return ''; // Team Over/Under - no badge needed
  }

  // A golfer, driver, tennis player or fighter has no club badge: the card's main image is their
  // country flag already (DKPLAT-606), and a logo built from their "team" code would 404.
  // The player API sends the sport on the fixture, not the question.
  const sport =
    question.sport_external_id ??
    (question as { sport_match_data?: { leagueId?: string } }).sport_match_data?.leagueId;
  if (usesCountryFlags(sport)) return '';

  if (config?.teamLogoUrl) {
    return normalizeTeamLogoUrl(config.teamLogoUrl);
  }

  // Fallback: try to generate URL from teamShortcode in config
  if (config?.teamShortcode) {
    const sport = question.sport_external_id?.toLowerCase() || 'nba';
    return generateTeamLogoUrl(sport, config.teamShortcode);
  }

  // Fallback: try option-level grading_data
  const firstOption = question.options?.[0];
  const gradingData = firstOption?.grading_data;
  if (gradingData?.teamShortcode) {
    const sport = question.sport_external_id?.toLowerCase() || 'nba';
    return generateTeamLogoUrl(sport, gradingData.teamShortcode);
  }

  return '';
}

/**
 * Generates a team logo URL from sport and team shortcode
 * Uses the same blob storage structure as the backend
 */
function generateTeamLogoUrl(sport: string, teamShortcode: string): string {
  const STORAGE_ACCOUNT = import.meta.env.VITE_BLOB_STORAGE_ACCOUNT_NAME || 'draftkingsdev';
  const CONTAINER = import.meta.env.VITE_BLOB_STORAGE_CONTAINER || 'picks-core';
  const normalizedSport = sport.toLowerCase();
  const normalizedShortcode = teamShortcode.toUpperCase();
  const configuredFolder = getTeamAssetFolder();

  // Contest-selected picks-core folder (AssetFolderSelector) wins over IP defaults.
  if (configuredFolder) {
    if (configuredFolder === 'helmet') {
      const helmetUrl = `https://${STORAGE_ACCOUNT}.blob.core.windows.net/${CONTAINER}/ncaafb/helmet/${normalizedShortcode}.png`;
      return isLowResImages() ? toLowResImageUrl(helmetUrl) : helmetUrl;
    }
    if (configuredFolder === 'teams') {
      const fileExt = getFileExtForSport(normalizedSport);
      const teamUrl = `https://${STORAGE_ACCOUNT}.blob.core.windows.net/${CONTAINER}/${normalizedSport}/teams/${normalizedShortcode}.${fileExt}`;
      return isLowResImages() ? toLowResImageUrl(teamUrl) : teamUrl;
    }
    const folderUrl = `https://${STORAGE_ACCOUNT}.blob.core.windows.net/${CONTAINER}/${normalizedSport}/${configuredFolder}/${normalizedShortcode}.png`;
    return isLowResImages() ? toLowResImageUrl(folderUrl) : folderUrl;
  }

  // NCAA Football: non-IP helmet artwork (PNG) under /helmet, not the standard
  // /teams/{code}.svg (DKPLAT-559). Matches backend DraftKingsTeamLogoProvider.
  if (normalizedSport === 'ncaafb') {
    const helmetUrl = `https://${STORAGE_ACCOUNT}.blob.core.windows.net/${CONTAINER}/ncaafb/helmet/${normalizedShortcode}.png`;
    return isLowResImages() ? toLowResImageUrl(helmetUrl) : helmetUrl;
  }

  // IP switch OFF: the logos under /teams are licensed artwork, so fall back to the
  // unlicensed team artwork — one PNG per team, named by the team code
  // (DKPLAT-357 NFL, DKPLAT-605 NBA). Same shape as the NCAAFB helmet case above.
  //
  // The folder differs per sport (NFL `jerseys`, NBA `team-jerseys`) so it comes from the
  // shared map in leagueIp rather than being spelled out here — the same map the stored-URL
  // rewrite uses, so the two chokepoints cannot drift apart. A sport with no entry keeps its
  // logo instead of getting a fabricated path.
  //
  // Checked here rather than at the call sites because this is the client-side
  // chokepoint every generated team mark passes through; putting it anywhere
  // else leaves a path that still emits a logo.
  const nonIpFolder = !isLeagueIp() ? nonIpTeamFolder(normalizedSport) : undefined;
  if (nonIpFolder) {
    const jerseyUrl = `https://${STORAGE_ACCOUNT}.blob.core.windows.net/${CONTAINER}/${normalizedSport}/${nonIpFolder}/${normalizedShortcode}.png`;
    return isLowResImages() ? toLowResImageUrl(jerseyUrl) : jerseyUrl;
  }

  // File extension varies by sport (matches backend DraftKingsTeamLogoProvider)
  const fileExt = getFileExtForSport(normalizedSport);

  const teamUrl = `https://${STORAGE_ACCOUNT}.blob.core.windows.net/${CONTAINER}/${normalizedSport}/teams/${normalizedShortcode}.${fileExt}`;
  // Low-res opt-in (DKPLAT-669): serve from the `-low` folder when enabled.
  return isLowResImages() ? toLowResImageUrl(teamUrl) : teamUrl;
}

/**
 * Extracts jersey/player image URL from OVER_UNDER question options
 * The jersey image is stored in option_third_image (or option_image as fallback)
 */
export function extractJerseyUrl(question: PickemQuestion): string {
  // Try option's third image first (jersey)
  const firstOption = question.options?.[0];
  if (firstOption?.option_third_image) {
    return normalizeTeamLogoUrl(firstOption.option_third_image);
  }

  // Fallback to option image
  if (firstOption?.option_image) {
    return normalizeTeamLogoUrl(firstOption.option_image);
  }

  // Final fallback to question image
  return question.question_image_en || '';
}

/**
 * Extracts team logo URLs from OVER_UNDER question
 * Checks sport_match_data first, then falls back to option grading_data
 */
export function extractTeamLogos(question: PickemQuestion): {
  homeTeamLogo: string;
  awayTeamLogo: string;
} {
  // First try sport_match_data
  if (question.sport_match_data?.homeTeamLogo || question.sport_match_data?.awayTeamLogo) {
    return {
      homeTeamLogo: question.sport_match_data.homeTeamLogo
        ? normalizeTeamLogoUrl(question.sport_match_data.homeTeamLogo)
        : '',
      awayTeamLogo: question.sport_match_data.awayTeamLogo
        ? normalizeTeamLogoUrl(question.sport_match_data.awayTeamLogo)
        : '',
    };
  }

  // Fallback: try to get from option grading_data fixtureData
  const firstOption = question.options?.[0];
  const fixtureData = firstOption?.grading_data?.fixtureData;
  if (fixtureData) {
    // Note: fixtureData may have team names but not logos
    // This is a structural limitation - logos need to come from sport_match_data
    return {
      homeTeamLogo: '',
      awayTeamLogo: '',
    };
  }

  return {
    homeTeamLogo: '',
    awayTeamLogo: '',
  };
}

export function extractTeamShortCodes(question: PickemQuestion): {
  homeTeamAbbreviation: string;
  awayTeamAbbreviation: string;
} {
  if (!fixtureHasOpponent(question.sport_match_data)) {
    return { homeTeamAbbreviation: '', awayTeamAbbreviation: '' };
  }
  return {
    homeTeamAbbreviation: question.sport_match_data?.homeTeamAbbreviation || '',
    awayTeamAbbreviation: question.sport_match_data?.awayTeamAbbreviation || '',
  };
}

/**
 * Extracts match time from OVER_UNDER question
 * Checks sport_match_data first, then falls back to option grading_data fixtureData
 */
export function extractMatchTime(question: PickemQuestion): string {
  // First try sport_match_data
  if (question.sport_match_data?.scheduledDate) {
    return formatMatchTime(question.sport_match_data.scheduledDate);
  }

  // Fallback: try to get from option grading_data fixtureData
  const firstOption = question.options?.[0];
  const fixtureData = firstOption?.grading_data?.fixtureData;
  if (fixtureData?.scheduledDate) {
    return formatMatchTime(fixtureData.scheduledDate);
  }

  return '';
}
